import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import { withTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { navigate } from "@reach/router";
import moment from "moment-timezone";

const styles = () => ({
  root: {
    padding: "16px 20px",
  },
});

class BinMovementHistory extends Component {
  constructor(props) {
    super(props);
    this.searchDebounceTimeout = null;
    this.state = {
      loading: false,
      records: [],
      boxId: "",
      boxName: "",
      searchtext: "",
      page: 0,
      rowsPerPage: 10,
      orderBy: "movement_created_at",
      order: "desc",
      totalRecords: 0,
    };
  }

  componentDidMount() {
    const searchParams = new URLSearchParams(window.location.search || "");
    const boxId = searchParams.get("boxId") || "";
    const boxName = searchParams.get("boxName") || "";

    this.setState(
      {
        boxId,
        boxName,
      },
      () => {
        if (boxId) {
          this.fetchMovementHistory();
        }
      }
    );
  }

  componentWillUnmount() {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = null;
    }
  }

  getAuthToken = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      return (
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken
      ) || "";
    } catch (error) {
      return "";
    }
  };

  getReportTimeZone = () => {
    try {
      let zone = sessionStorage.getItem("timeZone");
      if (zone && zone.length) {
        zone = zone.substring(zone.lastIndexOf(" ") + 1);
      }
      if (zone && moment.tz.names().indexOf(zone) !== -1) {
        return zone;
      }
    } catch (error) {
      // no-op
    }
    return moment.tz.guess();
  };

  formatCreatedAt = (value) => {
    if (!value) return "-";
    const date = moment(value);
    if (!date.isValid()) return "-";
    const lang = localStorage.getItem("i18nextLng");
    const dateFormat = lang === "en" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY hh:mm:ss A";
    return date.tz(this.getReportTimeZone()).format(dateFormat);
  };

  fetchMovementHistory = async () => {
    const { boxId, searchtext, page, rowsPerPage, orderBy, order } = this.state;
    const { t } = this.props;
    if (!boxId) return;

    this.setState({ loading: true });
    try {
      const token = this.getAuthToken();
      const url =
        process.env.REACT_APP_BASEURL +
        `fastlane-goliath/bin-movement-history?boxId=${encodeURIComponent(
          boxId
        )}&searchtext=${encodeURIComponent(searchtext)}&pageNo=${page}&pageSize=${rowsPerPage}&sortColumn=${encodeURIComponent(
          orderBy
        )}&sortOrder=${encodeURIComponent(order)}`;

      const response = await fetch(url, {
        method: "GET",
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      const records =
        result && result.data && Array.isArray(result.data.records)
          ? result.data.records
          : [];
      const totalRecords =
        result && result.data && Number.isFinite(result.data.total)
          ? result.data.total
          : records.length;

      this.setState({ records, totalRecords, loading: false });
    } catch (error) {
      this.setState({ loading: false });
      toast.error(
        (error && error.message) ||
          (t("Location.BinMovementHistoryLoadFailed") ||
            "Failed to load bin movement history.")
      );
    }
  };

  handleBack = () => {
    navigate("/home/manage/locations");
  };

  handleSearchChange = (event) => {
    const nextSearch = event.target.value || "";
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = null;
    }

    this.setState({
      searchtext: nextSearch,
      page: 0,
    });

    const trimmedSearch = nextSearch.trim();
    if (trimmedSearch.length === 0) {
      this.fetchMovementHistory();
      return;
    }
    if (trimmedSearch.length < 3) {
      return;
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.fetchMovementHistory();
    }, 400);
  };

  handleRequestSort = (property) => {
    this.setState(
      (prev) => {
        const isSameColumn = prev.orderBy === property;
        const nextOrder = isSameColumn && prev.order === "asc" ? "desc" : "asc";
        return {
          orderBy: property,
          order: nextOrder,
          page: 0,
        };
      },
      this.fetchMovementHistory
    );
  };

  handleChangePage = (_event, page) => {
    this.setState({ page }, this.fetchMovementHistory);
  };

  handleChangeRowsPerPage = (event) => {
    const nextRows = parseInt(event.target.value, 10);
    this.setState(
      {
        rowsPerPage: Number.isFinite(nextRows) ? nextRows : 10,
        page: 0,
      },
      this.fetchMovementHistory
    );
  };

  render() {
    const { t, classes } = this.props;
    const {
      loading,
      records,
      boxName,
      boxId,
      searchtext,
      page,
      rowsPerPage,
      order,
      orderBy,
      totalRecords,
    } = this.state;
    const headCells = [
      { id: "movement_id", label: t("Location.BMH.MovementId") || "Movement ID" },
      { id: "bin_id", label: t("Location.BMH.BinId") || "Bin ID" },
      { id: "from_position_id", label: t("Location.BMH.FromPositionId") || "From Position ID" },
      { id: "to_position_id", label: t("Location.BMH.ToPositionId") || "To Position ID" },
      { id: "order_id", label: t("Location.BMH.OrderId") || "Order ID" },
      { id: "operation", label: t("Location.BMH.Operation") || "Operation" },
      { id: "success", label: t("Location.BMH.Success") || "Success" },
      { id: "error_message", label: t("Location.BMH.ErrorMessage") || "Error Message" },
      { id: "movement_created_at", label: t("Location.BMH.CreatedAt") || "Created At" },
    ];

    return (
      <div className={classes.root}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t("Location.BinMovementHistory") || "Bin Movement History"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              {(t("Location.BinMovementHistoryFor") || "Location") +
                ": " +
                (boxName || boxId || "-")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={this.handleBack}>
              {t("Location.BackToManageLocations") || "Back to Manage Locations"}
            </Button>
            <Button variant="contained" onClick={this.fetchMovementHistory} disabled={loading || !boxId}>
              {t("Location.Refresh") || "Refresh"}
            </Button>
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            value={searchtext}
            onChange={this.handleSearchChange}
            placeholder={t("Location.BMH.SearchPlaceholder") || "Search by order id, operation, bin id, position id"}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <React.Fragment>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id}>
                      <Tooltip title={t("Global.Label.Filter.Sort") || "Sort"}>
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={order}
                          onClick={() => this.handleRequestSort(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t("Location.BinMovementHistoryEmpty") ||
                        "No bin movement history found for this location."}
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((row) => (
                    <TableRow key={`${row.movement_id}-${row.created_on || ""}`}>
                      <TableCell>{row.movement_id}</TableCell>
                      <TableCell>{row.bin_id || "-"}</TableCell>
                      <TableCell>{row.from_position_id || "-"}</TableCell>
                      <TableCell>{row.to_position_id || "-"}</TableCell>
                      <TableCell>{row.order_id || "-"}</TableCell>
                      <TableCell>{row.operation || "-"}</TableCell>
                      <TableCell>{typeof row.success === "boolean" ? (row.success ? "YES" : "NO") : "-"}</TableCell>
                      <TableCell>{row.error_message || "-"}</TableCell>
                      <TableCell>{this.formatCreatedAt(row.movement_created_at || row.created_on)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords || 0}
              page={page}
              onPageChange={this.handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(BinMovementHistory));



// WEBPACK FOOTER //
// ./src/components/bin-movement-history/index.js